'use client';

import type { Voice } from 'ml-text-service-client';
import { type MouseEventHandler, useState } from 'react';

import { usePlayAudio } from './usePlayAudio';

export interface UseVoiceCardProps {
    voiceId: Voice['voice_id'];
    previewUrl: string;
    selectedVoice: Voice['voice_id'] | null;
    playingVoiceId: Voice['voice_id'] | null;
    setSelectedVoice: (voiceId: Voice['voice_id']) => void;
    setPlayingVoiceId?: (voiceId: Voice['voice_id'] | null) => void;
}

export interface UseVoiceCardResult {
    isSelected: boolean;
    isPlaying: boolean;
    isHovered: boolean;
    showPlayControls: boolean;
    setIsHovered: (hovered: boolean) => void;
    handleCardClick: () => void;
    handlePlayPause: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Custom hook that consolidates shared voice card logic between VoiceCard and ContributorVoiceCard.
 *
 * Handles:
 * - Selection state (isSelected)
 * - Hover state for showing play controls
 * - Play/pause audio functionality via usePlayAudio
 * - Automatic pause when another voice starts playing
 */
export const useVoiceCard = ({
    voiceId,
    previewUrl,
    selectedVoice,
    playingVoiceId,
    setSelectedVoice,
    setPlayingVoiceId,
}: UseVoiceCardProps): UseVoiceCardResult => {
    const [isHovered, setIsHovered] = useState(false);

    const { isPlaying, handlePlayPause } = usePlayAudio(previewUrl, voiceId, playingVoiceId, setPlayingVoiceId);

    const isSelected = selectedVoice === voiceId;
    const showPlayControls = isHovered || isPlaying;

    const handleCardClick = (): void => {
        setSelectedVoice(voiceId);
    };

    return {
        isSelected,
        isPlaying,
        isHovered,
        showPlayControls,
        setIsHovered,
        handleCardClick,
        handlePlayPause,
    };
};
